import { mdiArrowLeft, mdiArrowRight, mdiBarcode, mdiCashClock } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, ButtonBase, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ReactComponent as CreditCard } from '../../../assets/credit-card.svg';
import { ReactComponent as DebitCard } from '../../../assets/debit-card.svg';
import { ReactComponent as Pix } from '../../../assets/pix-logo.svg';
import { useUserContext } from '../../../contexts/User.context';
import { TipoPagamento } from '../../../models/TipoPagamento';
import { TipoUsuario, UsuarioPosPagoState } from '../../../models/Usuario';
import { UsuarioService } from '../../../services';
import { Colors } from '../../../shared/colors';
import {
  getProvedorPagamento,
  getTipoPagamentoCredito,
  getTipoPagamentoDebito,
  getTipoPagamentoPix
} from '../../../shared/functions/tipoPagamento';
import { useAppSelector } from '../../../shared/hooks/redux';
import { paymentSelector } from '../../../shared/store/Pagamento/payment.selectors';
import { setTipo, setUsarYuno } from '../../../shared/store/Pagamento/payment.slice';
import Currency from '../../components/Currency';
import Loading from '../../components/Loading';

interface OpcaoPagamento {
  tipo: TipoPagamento;
  titulo: string;
  descricao: string;
}

interface Props {
  onBack: () => void;
}

interface CardTipoPagamentoProps extends OpcaoPagamento {
  selecionado: boolean;
  onSelect: (tipo: TipoPagamento) => void;
}

const CardTipoPagamento = ({
  tipo,
  titulo,
  descricao,
  selecionado,
  onSelect
}: CardTipoPagamentoProps) => {
  const icone = (() => {
    if (tipo === TipoPagamento.Pix || tipo === TipoPagamento.YunoPix)
      return <Pix width={28} height={28} fill="#32BCAD" />;
    if (tipo === TipoPagamento.CartaoCredito || tipo === TipoPagamento.YunoCredito)
      return <CreditCard width={30} height={30} />;
    if (tipo === TipoPagamento.CartaoDebito || tipo === TipoPagamento.YunoDebito)
      return <DebitCard width={30} height={30} />;
    return (
      <Icon
        path={tipo === TipoPagamento.Boleto ? mdiBarcode : mdiCashClock}
        size={1.2}
        color={Colors.primary.main}
      />
    );
  })();

  return (
    <ButtonBase
      onClick={() => onSelect(tipo)}
      aria-pressed={selecionado}
      sx={{
        width: '100%',
        minHeight: 82,
        p: 1.5,
        gap: 1.5,
        justifyContent: 'flex-start',
        textAlign: 'left',
        border: `1px solid ${selecionado ? Colors.primary.main : Colors.border.divider}`,
        borderRadius: 1,
        bgcolor: selecionado ? Colors.primary.light : Colors.white,
        '&.Mui-focusVisible': { outline: `2px solid ${Colors.primary.main}` }
      }}
    >
      <Box
        width={52}
        height={52}
        flexShrink={0}
        borderRadius="50%"
        bgcolor={selecionado ? '#DCEFFF' : Colors.gray3}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {icone}
      </Box>
      <Box flex={1} minWidth={0}>
        <Typography color={Colors.secondary} fontWeight={700} fontSize={16}>
          {titulo}
        </Typography>
        <Typography color={Colors.text.label} fontSize={13}>
          {descricao}
        </Typography>
      </Box>
      <Box
        width={22}
        height={22}
        flexShrink={0}
        borderRadius="50%"
        border={`1.5px solid ${selecionado ? Colors.primary.main : Colors.border.input}`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {selecionado && (
          <Box width={12} height={12} borderRadius="50%" bgcolor={Colors.primary.main} />
        )}
      </Box>
    </ButtonBase>
  );
};

const SelecionarTipoPagamento = ({ onBack }: Props) => {
  const dispatch = useDispatch();
  const pagamento = useAppSelector(paymentSelector);
  const [loading, setLoading] = useState(false);
  const [solicitacaoState, setSolicitacaoState] = useState<UsuarioPosPagoState>();
  const [tipoSelecionado, setTipoSelecionado] = useState<TipoPagamento | null>(null);

  const { userClaim } = useUserContext();
  const usuarioPJ = userClaim === TipoUsuario.PESSOA_JURIDICA;

  useEffect(() => {
    setLoading(true);
    UsuarioService.getTipoUsuarioPosPago()
      .then((state) => {
        setSolicitacaoState(state);
        dispatch(setUsarYuno(getProvedorPagamento(state.paymentMethods) === 'YUNO'));
      })
      .finally(() => setLoading(false));

    return () => {
      setLoading(false);
    };
  }, [dispatch]);

  if (loading) return <Loading show={loading} bgColor={Colors.white} />;

  const provedorPagamento = getProvedorPagamento(solicitacaoState?.paymentMethods);
  const opcoes: OpcaoPagamento[] = [];

  if (solicitacaoState?.paymentMethods?.mostrarPix)
    opcoes.push({
      tipo: getTipoPagamentoPix(provedorPagamento),
      titulo: 'Pix',
      descricao: 'Aprovação imediata'
    });
  if (solicitacaoState?.paymentMethods?.mostrarBoleto)
    opcoes.push({
      tipo: TipoPagamento.Boleto,
      titulo: 'Boleto à vista',
      descricao: 'Saldo liberado no próximo dia útil'
    });
  if (solicitacaoState?.paymentMethods?.mostrarCartaoCredito)
    opcoes.push({
      tipo: getTipoPagamentoCredito(provedorPagamento),
      titulo: 'Cartão de crédito',
      descricao: 'Pagamento com cartão de crédito'
    });
  if (solicitacaoState?.paymentMethods?.mostrarCartaoDebito)
    opcoes.push({
      tipo: getTipoPagamentoDebito(provedorPagamento),
      titulo: 'Cartão de débito',
      descricao: 'Débito no cartão'
    });
  if (usuarioPJ && solicitacaoState?.posPagoAtivo)
    opcoes.push({
      tipo: TipoPagamento.PJPosPago,
      titulo: 'Pós-Pago',
      descricao: 'Pagamento posterior via fatura'
    });

  const opcaoAtual = tipoSelecionado ?? opcoes[0]?.tipo ?? null;

  return (
    <Box width="100%" flex={1} minHeight={0} display="flex" flexDirection="column">
      <ButtonBase
        onClick={onBack}
        sx={{
          alignSelf: 'flex-start',
          color: Colors.primary.dark,
          gap: 0.5,
          mb: 1.5,
          ml: -0.5,
          p: 0.5,
          '&.Mui-focusVisible': { outline: `2px solid ${Colors.primary.main}` }
        }}
      >
        <Icon path={mdiArrowLeft} size={0.7} />
        <Typography fontSize={14}>Voltar</Typography>
      </ButtonBase>
      <Typography color={Colors.secondary} fontSize={20} fontWeight={700} textAlign="center">
        Como deseja adicionar <Currency value={pagamento.valor} />?
      </Typography>
      <Typography color={Colors.text.label} fontSize={14} textAlign="center" mb={2.5}>
        Escolha a forma de pagamento para concluir.
      </Typography>

      <Box display="flex" flexDirection="column" gap={1.5} flex={1} minHeight={0} overflowY="auto">
        {opcoes.map((opcao) => (
          <CardTipoPagamento
            key={opcao.tipo}
            {...opcao}
            selecionado={opcaoAtual === opcao.tipo}
            onSelect={setTipoSelecionado}
          />
        ))}
      </Box>

      <Box mt="auto" pt={3}>
        <Button
          fullWidth
          variant="contained"
          disabled={opcaoAtual === null}
          onClick={() => opcaoAtual !== null && dispatch(setTipo(opcaoAtual))}
          sx={{
            height: 48,
            display: 'flex',
            justifyContent: 'space-between',
            textTransform: 'none'
          }}
        >
          <Box width={24} />
          <Typography fontWeight={600}>Adicionar</Typography>
          <Icon path={mdiArrowRight} size={1} color={Colors.white} />
        </Button>
      </Box>
    </Box>
  );
};

export default SelecionarTipoPagamento;
